import { useState, useCallback, useMemo } from 'react';
import { useDevice, useSensorData, useProductCapabilities, useFieldPending, useMTCommand } from '@/hooks';
import { deviceService } from '@/api/services';
import { NAMTMessageType } from '@/types/enums';
import { isIntegratedSensor, isSensorType, resolveSensorTypeName } from '@/lib/sensor-map';
import { getCombinedAddress, getXPulseZoneSlots, findPendingMT } from '@/lib/sensor-commands';
import { StatusBadge } from '@/components/common/StatusBadge/StatusBadge';
import { PageSpinner } from '@/components/common/Spinner/Spinner';
import { Button } from '@/components/common/Button/Button';
import { Modal } from '@/components/common/Modal/Modal';
import { ToggleSwitch, SectionCard, PendingBadge } from '@/components/common/ConfigControls';
import { NevataSensorConfig } from '../configuration/NevataSensorConfig';
import { XPulseSensorConfig } from '../configuration/XPulseSensorConfig';

// Relocated out of ConfigurationPage.tsx's former "Wireless Sensors" tab into
// its own page, so wireless-sensor connectivity (offline indicator) has a
// dedicated nav item independent of the device's own System Settings /
// Geofence Sync configuration.
export function SensorsPage() {
  const { selectedDeviceInfo, selectedDevice, deviceInfoLoading, refreshDeviceInfo } = useDevice();
  const { sensorData } = useSensorData();
  const caps = useProductCapabilities();
  const deviceUUID = selectedDevice?.deviceProperties.deviceUUID ?? '';
  const globalSettings = selectedDeviceInfo?.globalSettings;
  const sensorMapList = useMemo(() => selectedDeviceInfo?.sensorMapList ?? [], [selectedDeviceInfo]);
  const { sendCommand, mtObjList, feedback, setFeedback } = useMTCommand(deviceUUID);
  const fieldPending = useFieldPending();

  const [pairingEnabledSteady, setPairingEnabledSteady] = useState(false);
  const [expandedSensorKey, setExpandedSensorKey] = useState<string | null>(null);
  const [unpairTarget, setUnpairTarget] = useState<{ msb: number; lsb: number; name: string } | null>(null);
  const [refreshingConfig, setRefreshingConfig] = useState(false);
  const isAdmin = selectedDeviceInfo?.userInfo?.isAdmin ?? false;

  const wirelessSensors = useMemo(
    () => sensorMapList.filter((s) => !isIntegratedSensor(s)),
    [sensorMapList],
  );

  // Own copy of securityZones + handleSetZoneCategory -- also used by
  // ConfigurationPage.tsx's DeviceZoneSettings block, which is a separate
  // page instance and can't share this one's state.
  const securityZones = useMemo(() => sensorData?.securitySensorlist ?? [], [sensorData]);

  const handleSetZoneCategory = useCallback(
    async (zoneNum: number, deviceCategory: number) => {
      const zoneRecord = securityZones.find((z) => z.zoneNum === zoneNum && z.sourceAddressLSB === 1);
      if (!zoneRecord) {
        setFeedback({
          type: 'error',
          message: `No matching zone record (address LSB 1) found for zone ${zoneNum} yet — the category can't be changed until the device reports one.`,
        });
        return;
      }
      try {
        await deviceService.setDeviceProperties({
          deviceUUID,
          containedObj: {
            sourceAddressMSB: zoneRecord.sourceAddressMSB,
            sourceAddressLSB: zoneRecord.sourceAddressLSB,
            zoneInstance: zoneRecord.zoneNum - 1,
            deviceCategory,
          },
        });
        setFeedback({ type: 'success', message: 'Zone category update sent.' });
      } catch {
        setFeedback({ type: 'error', message: 'Failed to update zone category.' });
        return;
      }
      await refreshDeviceInfo();
    },
    [securityZones, deviceUUID, refreshDeviceInfo, setFeedback],
  );

  const handleTogglePairing = useCallback(
    (turnOn: boolean) =>
      fieldPending.run('sensorPairing', turnOn, async () => {
        setPairingEnabledSteady(turnOn);
        await sendCommand(turnOn ? NAMTMessageType.ENABLE_SENSOR_PAIRING : NAMTMessageType.DISABLE_SENSOR_PAIRING);
      }),
    [sendCommand, fieldPending],
  );

  const handleUnpair = useCallback(async () => {
    if (!unpairTarget) return;
    await sendCommand(NAMTMessageType.UNPAIR_SENSOR, [
      getCombinedAddress(unpairTarget.msb, unpairTarget.lsb),
    ]);
    setUnpairTarget(null);
  }, [unpairTarget, sendCommand]);

  const handleRefreshDeviceConfig = useCallback(async () => {
    setRefreshingConfig(true);
    try {
      await sendCommand(NAMTMessageType.SENSOR_MAP_REQUEST);
      await sendCommand(NAMTMessageType.GET_SECURITY_DATA);
      await sendCommand(NAMTMessageType.GET_SENSOR_DATA_REQUEST);
      await sendCommand(NAMTMessageType.INIT_REQUEST);
    } finally {
      setRefreshingConfig(false);
    }
  }, [sendCommand]);

  if (deviceInfoLoading) return <PageSpinner />;

  if (!globalSettings) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Sensors</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage paired wireless sensors</p>
        </div>
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center dark:border-navy-600 dark:bg-navy-900">
          <p className="text-sm text-slate-400 dark:text-slate-500">No device selected or settings unavailable.</p>
        </div>
      </div>
    );
  }

  // Mobile never constructs the "Nautic Alert Wireless Expansion" section for
  // Insight/VTracker (DeviceConfigPageViewModel.cs:392-393) -- not just
  // hidden, the page itself shouldn't be reachable for those device types.
  if (!caps.supportsWirelessExpansion) {
    return (
      <div className="space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Sensors</h1>
          <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage paired wireless sensors</p>
        </div>
        <div className="rounded-xl border border-dashed border-slate-300 bg-white p-12 text-center dark:border-navy-600 dark:bg-navy-900">
          <p className="text-sm text-slate-400 dark:text-slate-500">Not available on this device.</p>
        </div>
      </div>
    );
  }

  const pendingPairingEnable = findPendingMT(mtObjList, NAMTMessageType.ENABLE_SENSOR_PAIRING);
  const pendingPairingDisable = findPendingMT(mtObjList, NAMTMessageType.DISABLE_SENSOR_PAIRING);
  const pendingPairing = pendingPairingEnable ?? pendingPairingDisable;
  const pairingEnabled =
    fieldPending.getOptimisticValue<boolean>('sensorPairing') ??
    (pendingPairing
      ? (pendingPairingEnable?.mtmsn ?? 0) > (pendingPairingDisable?.mtmsn ?? 0)
      : pairingEnabledSteady);

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Sensors</h1>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">Manage paired wireless sensors</p>
      </div>

      {feedback && (
        <div
          className={`rounded-lg border p-3 text-sm ${
            feedback.type === 'success'
              ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-900/20 dark:text-green-400'
              : 'border-red-200 bg-red-50 text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400'
          }`}
        >
          <div className="flex items-center justify-between">
            <span>{feedback.message}</span>
            <button type="button" onClick={() => setFeedback(null)} className="ml-3 text-xs font-medium underline">
              Dismiss
            </button>
          </div>
        </div>
      )}

      {/* Device-wide controls */}
      <div className="flex flex-wrap items-center gap-3">
        {isAdmin && (
          <Button variant="primary" onClick={handleRefreshDeviceConfig} loading={refreshingConfig}>
            {refreshingConfig ? 'Refreshing' : 'Refresh Configuration'}
          </Button>
        )}
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500 dark:text-slate-400">Sensor Pairing</span>
          <ToggleSwitch enabled={pairingEnabled} loading={false} onToggle={() => handleTogglePairing(!pairingEnabled)} />
          {(fieldPending.isPending('sensorPairing') || pendingPairing) && <PendingBadge />}
        </div>
        <div className="ml-auto flex items-center gap-2">
          <span className="text-xs text-slate-500 dark:text-slate-400">Auto-Rescan</span>
          <ToggleSwitch
            enabled={fieldPending.getOptimisticValue<boolean>('coordAutoRescan') ?? (globalSettings.coordAutoRescanEnabled ?? false)}
            loading={false}
            onToggle={() => {
              setFeedback(null);
              fieldPending.run('coordAutoRescan', !(globalSettings.coordAutoRescanEnabled ?? false), () =>
                sendCommand(
                  globalSettings.coordAutoRescanEnabled
                    ? NAMTMessageType.MT_MESSAGE_SET_COORD_AUTO_RESCAN_DISABLED
                    : NAMTMessageType.MT_MESSAGE_SET_COORD_AUTO_RESCAN_ENABLED,
                ),
              );
            }}
          />
          {(fieldPending.isPending('coordAutoRescan') ||
            findPendingMT(mtObjList, NAMTMessageType.MT_MESSAGE_SET_COORD_AUTO_RESCAN_ENABLED) ||
            findPendingMT(mtObjList, NAMTMessageType.MT_MESSAGE_SET_COORD_AUTO_RESCAN_DISABLED)) && <PendingBadge />}
        </div>
      </div>

      {/* Single dynamic sensor list -- one row per paired wireless sensor, regardless of
          type, mirroring the mobile app's unified "Nautic Alert Wireless Expansion" list
          (DeviceConfigPageViewModel.refreshSensorManagement) rather than separate
          per-type sections. */}
      <SectionCard title="Wireless Sensors">
        {wirelessSensors.length === 0 ? (
          <p className="py-6 text-center text-sm text-slate-400 dark:text-slate-500">No wireless sensors discovered.</p>
        ) : (
          <div className="space-y-3">
            {wirelessSensors.map((sensor) => {
              const key = `${sensor.sourceAddressMSB}:${sensor.sourceAddressLSB}`;
              const isExpanded = expandedSensorKey === key;
              const onToggleExpand = () => setExpandedSensorKey(isExpanded ? null : key);

              if (isSensorType(sensor, 'LOOKOUT_SENSOR_TYPE')) {
                return (
                  <XPulseSensorConfig
                    key={key}
                    sensor={sensor}
                    zoneSlots={getXPulseZoneSlots(sensor, securityZones)}
                    remoteSirenSyncMode={globalSettings.remoteSirenSyncMode}
                    mtObjList={mtObjList}
                    isExpanded={isExpanded}
                    onToggleExpand={onToggleExpand}
                    sendCommand={sendCommand}
                    onUnpairRequest={setUnpairTarget}
                    onSetZoneCategory={handleSetZoneCategory}
                  />
                );
              }

              if (isSensorType(sensor, 'WATER_SENSOR_TYPE')) {
                const nev = (sensorData?.nevataSensorList ?? []).find(
                  (n) => n.sourceAddressMSB === sensor.sourceAddressMSB && n.sourceAddressLSB === sensor.sourceAddressLSB,
                );
                return (
                  <NevataSensorConfig
                    key={key}
                    sensor={sensor}
                    nev={nev}
                    mtObjList={mtObjList}
                    isExpanded={isExpanded}
                    onToggleExpand={onToggleExpand}
                    sendCommand={sendCommand}
                    onUnpairRequest={setUnpairTarget}
                  />
                );
              }

              // Unrecognized sensor type -- still shown (never silently dropped), just
              // without type-specific configuration controls.
              return (
                <div
                  key={key}
                  className="flex items-center justify-between rounded-lg border border-slate-200 px-4 py-3 dark:border-navy-700"
                >
                  <div>
                    <span className="text-sm font-semibold text-slate-800 dark:text-white">{sensor.sensorName}</span>
                    <span className="ml-2 text-xs text-slate-400 dark:text-slate-500">
                      Addr: {sensor.sourceAddressMSB}:{sensor.sourceAddressLSB}
                    </span>
                  </div>
                  <StatusBadge label={resolveSensorTypeName(sensor.sensorType)} variant="neutral" />
                </div>
              );
            })}
          </div>
        )}
      </SectionCard>

      {/* Unpair confirmation modal */}
      <Modal isOpen={!!unpairTarget} onClose={() => setUnpairTarget(null)} title="Unpair Sensor">
        <p className="text-sm text-slate-600 dark:text-slate-400">
          Are you sure you want to unpair{' '}
          <strong className="text-slate-800 dark:text-white">{unpairTarget?.name}</strong>?
          This will remove the sensor and all its zone configurations from the device.
        </p>
        <div className="mt-6 flex justify-end gap-3">
          <Button variant="secondary" onClick={() => setUnpairTarget(null)}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleUnpair}>
            Unpair
          </Button>
        </div>
      </Modal>
    </div>
  );
}
